import { BaseView } from "./base-view";
import { Navigation } from "../navigation";
import { DemandDetailView } from "./demand-detail-view";
import { AssignmentDemand } from "../models/AssignmentDemand";
import type mdbTypes from '../ext/mdb.min.js';
declare const mdb: typeof mdbTypes
import { CellComponent, ColumnDefinition, RowComponent, TabulatorFull } from "tabulator-tables";
import _ from "lodash";
import { PageContext } from "../page-context";
import { Employee } from "../models/Employee";
import { Project } from "../models/Project";
import { ProjectAssignmentChange } from "../models/ProjectAssignmentChange";
import { closeModal, convertHtmlToNode, createEmployeePhotoElement, createNameIconElementWith2Initials, fireBaseLog, formatLocaleDateString, getWeekNumber, getWeekYear, replaceAccentedCharacters, showModal, showToast } from "../common";
import { globalApp } from "../app";
import { CalendarWeek } from "../models/CalendarWeek";
import { AssignmentDemandRole } from "../enums/AssignmentDemandRole";
import { DemandCopyView } from "./demand-copy-view";
import { showResourceDetailModal } from "./resource-detail-view";


export class ProjectManagementView extends BaseView {
    static title = "Project Management";
    static icon = "bi bi-bar-chart-steps";

    /** @type {string} */
    filteredView: keyof typeof this.filteredViews = this.context.params.filteredView || "active";

    /** @type {Map<string, Employee>} */
    allEmployeesMap: Map<string, Employee> = new Map<string, Employee>();

    /** @type {AssignmentDemand[]} */
    assignments: AssignmentDemand[] = [];

    filteredViews = {
        "active": (data: AssignmentDemand) => {
            if (!data.assignments)
                return true;
            return !!(data.project && data.project.IsActive());
        },
        "unassigned": (data: AssignmentDemand) => {
            if (!data.assignments)
                return true;
            return data.assignments && data.assignments.length === 0 && !!(data.project && data.project.IsActive());
        },
        "all": () => true,
        "my": (data: AssignmentDemand) => {
            if (!data.assignments)
                return true;
            return data.assignments && data.assignments.some((a) => a.responsible === this.context.user.userId || a.employeeId === this.context.user.userId);
        }
    }
    projectNames: Project[] = [];
    keywords: string[] = [];
    unit: string;
    timescale: string;
    changedAssignmentsBefore: ProjectAssignmentChange[];
    changedAssignmentsAfter: AssignmentDemand[];
    lastModifiedCell: CellComponent | undefined
    popstateHandler!: (event: any) => void;
    maxHours: number = 40;
    /**
     * @constructor
     * @param {PageContext} context - The context of the application.
     * @param {string} [container=".main"] - The container to render the view in.
     */
    constructor(context: PageContext, container = ".main") {
        super(context, container);

        this.changedAssignmentsBefore = [];
        this.changedAssignmentsAfter = [];
        this.lastModifiedCell = undefined;

        this.context.store.setTargetDate(new Date());

        let versionCheck = this.checkVersionAsync();

        Promise.all([versionCheck]).then(() => {
            let dataLoad = [
                context.store.getProjectAssignments(),
                context.store.getAllEmployeesMap(),
                this.context.store.getProjects()
            ]

            Promise.all(dataLoad).then((values) => {
                this.assignments = values[0] as AssignmentDemand[];
                this.allEmployeesMap = values[1] as Map<string, Employee>;
                this.projectNames = values[2] as Project[];
                this.assignments.forEach(element => {
                    //override the currently returned data to split project name and id again (role is used as column for project id)
                    if (element.project) {
                        element.projectName = element.project.projectName;
                        element.role = element.project.id.length === 0 ? "-" : element.project.id;
                    }
                });

                this.projectNames.map((project) => {
                    project.keywords ? project.keywords.forEach((keyword) => {
                        if (this.keywords.indexOf(keyword) === -1) {
                            this.keywords.push(keyword);
                        }
                    }) : null;
                });

                this.onDataLoaded();
            });
        });

        this.unit = context.params.unit || context.userPreferences.getPreference("unit") || "hrs";
        this.timescale = context.params.timescale ? context.params.timescale : "week";
        this.context = context;
    }

    /**
     * Render the view
     */
    connectedCallback(): this {
        return super.connectedCallback();
    }

    onDataLoaded() {
        this.innerHTML = `
        <div>
            <h1><i class="${ProjectManagementView.icon}"></i> Project Management</h1>
            <p>
                Project Management is a view to help you manage your projects.
                <a class="help-page-link-icon" href="#/help#project-management" title="Help page">
                    <i class="bi bi-info-circle"></i>
                </a>
            </p>

            <div class="row">
                <div class="col-auto me-auto table-toolbar-container">                
                    <button accesskey="e" id="earlier-date" class="btn-datePick btn btn-sm btn-secondary" href="#" title="Jump to earlier date"><i class="bi bi-arrow-left"></i></button>
                    <button accesskey="t" id="datepicker" class="btn-datePick btn btn-sm btn-secondary" href="#" title="Select Date"><i class="bi bi-calendar-date"></i></button>
                    <button accesskey="l" id="later-date" class="btn-datePick btn btn-sm btn-secondary" href="#" title="Jump to later date"><i class="bi bi-arrow-right"></i></button>                    
                    <div id="unit" class="visually-hidden btn-group btn-group-sm btns-timescale px-2" role="group" aria-label="Timescale">
                        <button type="button" accesskey="w" class="btn btn-${this.timescale === "week" ? "" : "outline-"}secondary">week</button>
                        <button type="button" accesskey="m" class="btn btn-${this.timescale === "month" ? "" : "outline-"}secondary">month</button>
                    </div>

                    <button accesskey="r" class="btn btn-sm btn-outline-secondary btn-refresh" title="Reload data"><i class="bi bi-arrow-clockwise"></i></button>

                    <div class="btn-group btn-group-sm btn-filteredViews" role="group"></div>
                </div>

                <div class="col-auto table-toolbar-container">
                    <button accesskey="e" class="btn btn-sm btn-outline-secondary btn-export-csv" title="Export CSV"><i class="bi bi-cloud-arrow-down"></i></button>
                    <button accesskey="c" class="btn btn-sm btn-outline-secondary btn-toggle-cols" title="Show columns"><i class="bi bi-layout-three-columns"></i></button>
                    <div id="unit" class="btn-group btn-group-sm btns-units mx-2" role="group" aria-label="Units">
                        <button type="button" accesskey="h" class="btn btn-${this.unit === "hrs" ? "" : "outline-"}secondary" title="Hours">hrs</button>
                        <button type="button" accesskey="p" class="btn btn-${this.unit === "pd" ? "" : "outline-"}secondary" title="Project days">pd</button>
                        <button type="button" accesskey="z" class="btn btn-${this.unit === "%" ? "" : "outline-"}secondary" title="Percentage">%</button>
                    </div>
                </div>
            </div>
        </div>
        
        <div id="projects-table" class="remaining-height"></div>
        `;

        let elem = $(this);

        elem.find(".btn-export-csv").click((e) => {
            this.table.download("csv", "export.csv");
        });

        elem.find(".btn-refresh").click((e) => {
            this.refreshTableData();
        });

        elem.find(`.btns-units`).click((e) => {
            this.unit = $(e.target).text();
            elem.find(`.btns-units button`).removeClass(`btn-secondary`).addClass(`btn-outline-secondary`);
            $(e.target).removeClass(`btn-outline-secondary`).addClass(`btn-secondary`);
            this.context.params.unit = this.unit;
            Navigation.navigateHash(null, this.context.params, true);
            this.context.userPreferences.setPreference("unit", this.unit);
            this.table.redraw(true);
        });

        elem.find(`.btns-timescale`).click((e) => {
            this.timescale = $(e.target).text();
            elem.find(`.btns-timescale button`).removeClass(`btn-secondary`).addClass(`btn-outline-secondary`);
            $(e.target).removeClass(`btn-outline-secondary`).addClass(`btn-secondary`);
            this.context.params.timescale = this.timescale;
            Navigation.navigateHash(null, this.context.params, true);
            this.table.replaceData(this.table.getData());
            showToast("Not implemented yet", "Timescale changed to " + this.timescale);
        });

        const btnfilteredViews = elem.find(".btn-filteredViews");
        Object.keys(this.filteredViews).forEach((filterName) => {
            const filterButton = $(`<button class="btn btn-sm btn-${this.filteredView === filterName ? "" : "outline-"}secondary" data-view="${filterName}">${filterName}</button>`);
            filterButton.click((e) => {
                $(e.target).parent().children().removeClass("btn-secondary").addClass("btn-outline-secondary");
                $(e.target).removeClass("btn-outline-secondary").addClass("btn-secondary");
                this.filteredView = $(e.target).data("view");
                this.applyFilteredView();
            });
            btnfilteredViews.append(filterButton);
        });
        const options = {
            dateFormat: "mm/dd/yy",
            todayHighlight: true,
            autoclose: true,
            calendarWeeks: true,
            todayBtn: true,
            weekStart: 1,
        };

        elem.find('#datepicker').datepicker(options).on('changeDate', (e) => {
            const selectedDate = $("#datepicker").datepicker("getDate");
            this.targetNewDate(selectedDate);

        });

        elem.find(`#earlier-date`).click((e) => {
            let currentTargetDate = this.context.store.targetDate;
            let newTargetDate = new Date(currentTargetDate.getFullYear(), currentTargetDate.getMonth() - 2, currentTargetDate.getDate());
            elem.find('#datepicker').datepicker('setDate', newTargetDate);
        });

        elem.find(`#later-date`).click((e) => {
            let currentTargetDate = this.context.store.targetDate;
            let newTargetDate = new Date(currentTargetDate.getFullYear(), currentTargetDate.getMonth() + 2, currentTargetDate.getDate());
            elem.find('#datepicker').datepicker('setDate', newTargetDate);
        });

        this.updateSaveCancelButtons();

        this.addTable();
        this.columnToggler($(".btn-toggle-cols")[0], this.table);

        //cancel manually planned weeks on page change
        this.popstateHandler = (event) => {
            let hoverBox = document.getElementById('hoverBox');
            if (hoverBox && hoverBox.parentNode) {
                hoverBox.parentNode.removeChild(hoverBox);
            }
            if (this.changedAssignmentsAfter.length != 0) {
                this.cancelManuallyPlannedWeeks();
                window.removeEventListener('popstate', this.popstateHandler);
            };
        }

        this.context.store.assignmentChangeUnsubscribeAll();
        this.context.store.assignmentChangeSubscribe(this.subscribedAssignmentChangeHandler);
        window.addEventListener('popstate', this.popstateHandler);
    }

    refreshTableData() {
        this.showLoading();

        this.context.store.assignmentChangeUnsubscribe(this.subscribedAssignmentChangeHandler);
        this.context.store.clearSubscribedAssignments().then((x) =>{
            this.context.store.getProjectAssignments().then((data) => {
                this.assignments = data;
                this.assignments.forEach(element => {
                    if (element && element.project) {
                        //override the currently returned data to split project name and id again (role is used as column for project id)
                        element.projectName = element.project.projectName;
                        element.role = element.project.id.length === 0 ? "-" : element.project.id;
                    }
                });
                this.table.replaceData(data);
                this.context.store.assignmentChangeSubscribe(this.subscribedAssignmentChangeHandler);
                this.hideLoading();
                this.calculateRowsForEachFilter();
    
                this.changedAssignmentsBefore = [];
                this.changedAssignmentsAfter = [];
                this.updateSaveCancelButtons();
            });
        });
    }

    updateSaveCancelButtons(cell: CellComponent | null = null) {
        let hoverBox = document.getElementById('hoverBox');
        if (this.changedAssignmentsAfter.length == 0 || !cell) {
            if (hoverBox)
                hoverBox.style.display = 'none';
            return;
        }

        if (hoverBox)
            hoverBox.style.display = 'block';
        else {
            hoverBox = document.createElement('div');
            hoverBox.id = 'hoverBox';
            hoverBox.style.cssText = 'position:absolute; background-color:white; padding:10px; border:1px solid black; z-index:1000; cursor:move; border-radius:10px';

            let createButton = (text: string, className: string, clickHandler: { (e: any): void; (this: HTMLButtonElement, ev: MouseEvent): any; }) => {
                let button = document.createElement('button');
                button.textContent = text;
                button.className = className;
                button.style.margin = '5px';
                button.addEventListener('click', clickHandler);
                if (hoverBox)
                    hoverBox.appendChild(button);
            };

            let buttons = [
                {
                    text: 'Apply till end',
                    className: 'btn btn-sm btn-primary btn-apply-till-end-manually-planned-week',
                    clickHandler: () => {
                        if (confirm("Are you sure you want to apply same utilisation till the end date for the last modified assignment?"))
                            this.applyTillEndLastAssignment();
                    }
                },
                {
                    text: 'Save',
                    className: 'btn btn-sm btn-primary btn-save-manually-planned-weeks',
                    clickHandler: () => {
                        let assignmentNamePlaceholder = '';
                        try {
                            assignmentNamePlaceholder = this.changedAssignmentsAfter.map(a => {
                                const project = this.projectNames.find((project) => project.id === a.projectId);
                                return a.projectId + "-" + (project ? project.projectName : "Project Name Unassigned");
                            }).join(",\n");
                        } catch (error) {
                            const err = error as Error;
                            if (err.name == "TypeError") { assignmentNamePlaceholder = this.changedAssignmentsAfter.map(a => a.projectId + "- Name Unassigned").join(",\n") };
                        }
                        if (!confirm("Are you sure you want to save calendar week planning for this assignments?\n" + assignmentNamePlaceholder)) {
                            showToast(`${assignmentNamePlaceholder}: Assignment save cancelled`, "success");
                            return;
                        }
                        this.saveManuallyPlannedWeeks();
                    }
                },
                {
                    text: 'Cancel',
                    className: 'btn btn-sm btn-danger btn-cancel-manually-planned-weeks',
                    clickHandler: () => {
                        let assignmentNamePlaceholder = '';
                        try {
                            assignmentNamePlaceholder = this.changedAssignmentsAfter.map(a => {
                                const project = this.projectNames.find((project) => project.id === a.projectId);
                                return a.projectId + "-" + (project ? project.projectName : "Project Name Unassigned");
                            }).join(",\n");
                        } catch (error) {
                            const err = error as Error;
                            if (err.name == "TypeError") { assignmentNamePlaceholder = this.changedAssignmentsAfter.map(a => a.projectId + " - Name Unassigned").join(",\n") };
                        }
                        if (!confirm("Are you sure you want to cancel calendar week planning for this assignments?\n" + assignmentNamePlaceholder)) {
                            showToast(`${assignmentNamePlaceholder}: Assignment suggest cancelled`, "success");
                            return;
                        }
                        this.cancelManuallyPlannedWeeks();
                    }
                }
            ];

            buttons.forEach(button => createButton(button.text, button.className, button.clickHandler));
            document.body.appendChild(hoverBox);

            var dragStartX: number, dragStartY: number;
            hoverBox.addEventListener('mousedown', (event) => {
                if (hoverBox) {
                    dragStartX = event.clientX - hoverBox.offsetLeft;
                    dragStartY = event.clientY - hoverBox.offsetTop;
                }
                document.addEventListener('mousemove', drag);
                document.addEventListener('mouseup', endDrag);
            });

            const drag = (event: MouseEvent) => {
                if (hoverBox) {
                    let newLeft = Math.min(Math.max(event.clientX - dragStartX, 0), window.innerWidth - hoverBox.offsetWidth - 30);
                    let newTop = Math.min(Math.max(event.clientY - dragStartY, 0), window.innerHeight - hoverBox.offsetHeight);
                    hoverBox.style.left = `${newLeft}px`;
                    hoverBox.style.top = `${newTop}px`;
                }
            }

            const endDrag = () => {
                document.removeEventListener('mousemove', drag);
                document.removeEventListener('mouseup', endDrag);
            }
        }

        let rect = cell.getElement().getBoundingClientRect();
        let hoverBoxWidth = hoverBox.offsetWidth;
        let hoverBoxHeight = hoverBox.offsetHeight;

        // Ensure the hoverBox does not go beyond the right edge of the screen + 30px to not colapse the hoverBox
        if (rect.left + hoverBoxWidth > window.innerWidth) {
            hoverBox.style.left = `${window.innerWidth - hoverBoxWidth - 40}px`;
        } else {
            hoverBox.style.left = `${rect.left}px`;
        }

        // Ensure the hoverBox does not go beyond the bottom edge of the screen
        if (rect.bottom + hoverBoxHeight > window.innerHeight) {
            hoverBox.style.top = `${window.innerHeight - hoverBoxHeight}px`;
        } else {
            hoverBox.style.top = `${rect.bottom}px`;
        }
    }

    async onRowClick(e: UIEvent, cell: CellComponent) {
        let row = cell.getRow();
        let data = row.getData();
        row.getTable().deselectRow();
        row.select();
        if (data.assignments) {
            if (!(<HTMLElement>e.target).classList.contains("bi-front") || (<HTMLElement>e.target).classList.contains("copy-button")) {
                row.treeToggle();
            }

            return;
        }
        Navigation.navigateHash("/project-management/detail", { id: data.id }, true);
        var ctx = Object.assign(this.context, { data: data });
        await DemandDetailView.showAsModal(ctx);
        Navigation.restorePreviousHashUrl();

        this.changedAssignmentsBefore = [];
        this.changedAssignmentsAfter = [];
        this.updateSaveCancelButtons();
    }

    targetNewDate(newTargetDate: Date) {
        this.showLoading();
        if (this.changedAssignmentsAfter.length != 0) {
            this.cancelManuallyPlannedWeeks();
            window.removeEventListener('popstate', this.popstateHandler);
        };
        this.context.store.setTargetDate(newTargetDate);
        this.context.store.getProjectAssignments().then((data) => {
            let currentFilters = this.table.getHeaderFilters();
            this.assignments = data;
            this.table.destroy();
            this.addTable();

            this.table.on("tableBuilt", () => {
                for (let filter of currentFilters) {
                    this.table.setHeaderFilterValue(filter.field, filter.value);
                }
            });

            this.hideLoading();
        });
    }

    getColumns() {
        let columns: ColumnDefinition[] = [
            {
                title: "Project / Role",
                field: "role",
                frozen: true,
                width: 200,
                headerFilter: "input",
                headerFilterFunc: function (headerValue, rowValue, rowData) {
                    if (rowData.assignments) {
                        return rowValue?.toLowerCase().includes(headerValue?.toLowerCase());
                    }
                    return true;
                },
                cellClick: this.onRowClick.bind(this),
                tooltip: true,
                vertAlign: "middle",
                formatter: this.copyBtnFormatter.bind(this)
            },
            {
                title: "Project name",
                field: "projectName",
                frozen: true,
                width: 200,
                headerFilter: "input",
                headerFilterFunc: function (headerValue, rowValue, rowData) {
                    if (rowData.assignments) {
                        if (!rowValue || !headerValue) return false;
                        return replaceAccentedCharacters(rowValue.toLowerCase()).includes(replaceAccentedCharacters(headerValue.toLowerCase()));
                    }
                    return true;
                },
                cellClick: this.onRowClick.bind(this),
                tooltip: true
            },
            {
                title: "Originating project",
                field: "originatingProjectId",
                visible: false,
                frozen: true,
                width: 200,
                headerFilterFunc: this.customOriginatingProjectFilter,
                headerFilter: "input",
                cellClick: this.onRowClick.bind(this),
                tooltip: true
            },
            {
                title: "Employee",
                field: "employeeId",
                frozen: true,
                width: 200,
                headerFilter: "input",
                headerFilterFunc: this.customEmployeeFilter,
                formatter: this.buttonFormatter,
                sorter: (a, b, aRow, bRow) => {
                    if (aRow.getData().assignments && bRow.getData().assignments) {
                        return aRow.getData().assignments.length - bRow.getData().assignments.length;
                    }
                    return a ? a.toString().localeCompare(b ? b.toString() : "") : "".localeCompare(b ? b.toString() : "");
                },
                cellClick: this.onRowClick.bind(this),
                tooltip: true
            },
            {
                title: "Classification",
                field: "projectName",
                width: 200,
                headerFilter: "list",
                headerFilterParams: {
                    values: this.keywords
                },
                visible: false,
                tooltip: true,
                formatter: (cell) => {
                    const keywords = this.projectNames.find((project) => project.projectName === cell.getValue())?.keywords || [];
                    if (keywords.length === 0) return "";
                    return keywords.map((keyword: string) => `<span class="badge bg-secondary">${keyword}</span>`).join(" ");
                },
                headerFilterFunc: (headerValue, rowValue, rowData) => {
                    const keywords = this.projectNames.find((project) => project.projectName === rowValue)?.keywords || [];
                    return keywords.some((keyword) => keyword.toLowerCase().includes(headerValue.toLowerCase()));
                }
            },
            {
                title: "PM 1",
                field: "project.lead_0",
                width: 100,
                frozen: true,
                headerFilter: "input",
                headerFilterFunc: function (headerValue, rowValue, rowData) {
                    if (rowData.assignments) {
                        return rowValue?.toLowerCase().includes(headerValue?.toLowerCase());
                    }
                    return true;
                },
                formatter: (cell, formatterParams) => {
                    const employee = this.allEmployeesMap.get(cell.getValue());
                    return employee ? `${employee.employeeName}` : cell.getValue();
                },
                vertAlign: "middle",
                tooltip: true,
                visible: false
            },
            {
                title: "PM 2",
                field: "project.lead_1",
                width: 100,
                headerFilter: "input",
                headerFilterFunc: function (headerValue, rowValue, rowData) {
                    if (rowData.assignments) {
                        return rowValue?.toLowerCase().includes(headerValue?.toLowerCase());
                    }
                    return true;
                },
                frozen: true,
                formatter: (cell) => {
                    const employee = this.allEmployeesMap.get(cell.getValue());
                    return employee ? `${employee.employeeName}` : cell.getValue();
                },
                vertAlign: "middle",
                tooltip: true,
                visible: false
            },
            {
                title: "Start",
                field: "startDate",
                formatter: (cell) => formatLocaleDateString(cell.getValue()),
                sorter: (a, b) => { return new Date(a).getTime() - new Date(b).getTime() },
                width: 100,
                frozen: true,
                vertAlign: "middle",
                tooltip: true,
                visible: false
            },
            {
                title: "End",
                field: "endDate",
                formatter: (cell) => formatLocaleDateString(cell.getValue()),
                sorter: (a, b) => { return new Date(a).getTime() - new Date(b).getTime() },
                width: 100,
                frozen: true,
                vertAlign: "middle",
                tooltip: true,
                visible: false
            },
        ];

        const cwColsFormatterParams = {
            maxHours: 40,
            employeesMap: this.allEmployeesMap
        };
        const cwColumns = this.addCalendarWeekCols(this.context.store.getCalendarWeeks(), this.context.user.hasEditPermission(), cwColsFormatterParams);
        return columns.concat(cwColumns);
    }

    /**
     * Add table to the view
     */
    addTable() {
        var table = new TabulatorFull("#projects-table", {
            data: this.assignments,
            height: 200,
            layout: "fitColumns",
            dataTree: true,
            dataTreeStartExpanded: false,
            dataTreeChildField: "assignments",
            columns: this.getColumns(),
            initialSort: [
                { column: "role", dir: "desc" },
            ]
        });

        table.on("tableBuilt", async () => {
            this.applyFilteredView();
            this.jumpToWeek();

            // expand the row if we have an assignmentId in the context
            if (this.context.params.id) {
                for (const row of table.getRows()) {
                    var parentData = row.getData();
                    parentData.assignments.forEach(async (a: { id: any; }, idx: string | number) => {
                        if (a.id === this.context.params.id) {
                            row.treeExpand();
                            //TODO check if this was working?
                            //table.scrollToRow(row);
                            row.select();
                            var ctx = Object.assign(this.context, { data: a });
                            var result = await DemandDetailView.showAsModal(ctx);
                            Navigation.restorePreviousHashUrl();

                            if (result) {
                                if (parentData.assignments) 
                                    parentData.assignments[idx] = result;
                                this.context.store.calculateTotalWeekly(parentData as AssignmentDemand);
                                row.update(parentData);
                            }
                        }
                    });
                }
            } else {
                closeModal();
            }
            this.calculateRowsForEachFilter();
        });

        table.on("cellEditing", (cell) => {
            const row = cell.getRow();
            const data = row.getData();

            if (data.role == AssignmentDemandRole.AbsenceAssigmentRole || !this.isEditable(cell)) {
                cell.cancelEdit();
                return;
            }

            if (!this.changedAssignmentsBefore.find(a => a.id === data.id)) {
                let copy = new ProjectAssignmentChange({ id: data.id, manuallyPlanned: data.manuallyPlanned });
                for (let key in data.plannedWeeks) {
                    if (key.startsWith('cw_')) {
                        copy[key] = data.plannedWeeks[key];
                    }
                }
                this.changedAssignmentsBefore.push(copy);
            }
        });

        table.on("cellEdited", async (cell) => {
            //cell - cell component
            const row = cell.getRow();
            const data = row.getData() as AssignmentDemand;

            if (!data.manuallyPlanned) {
                showToast(`You have started manually planning for assignment: ${data.projectId}}`, "success");
            }

            data.manuallyPlanned = true;
            data.pdTotalPlanned = 0;
            [data.startDate, data.endDate] = [new Date(9999, 11, 31), new Date(0, 0, 1)];

            //calculate first monday and last friday
            Object.keys(data.plannedWeeks).filter(key => key.startsWith("cw_") && data.plannedWeeks[key] != 0).forEach(key => {
                data.pdTotalPlanned += data.plannedWeeks[key];
                const [_, weekNumber, year] = key.split("_");
                const [firstDay, lastDay] = [this.getUTCWeekDay(Number(year), Number(weekNumber), 1), this.getUTCWeekDay(Number(year), Number(weekNumber), 5)];
                data.startDate = data.startDate ? new Date(Math.min(data.startDate.getTime(), Number(firstDay))) : firstDay;
                data.endDate = data.endDate ? new Date(Math.max(data.endDate.getTime(), Number(lastDay))) : lastDay;
            });

            data.pdTotalPlanned = Math.ceil(data.pdTotalPlanned / 8);

            if (!this.changedAssignmentsAfter.find(a => a.id === data.id)) {
                this.changedAssignmentsAfter.push(data);
            }

            this.lastModifiedCell = cell;

            this.updateSaveCancelButtons(cell);

            const parentRow = row.getTreeParent();
            if (parentRow) {
                const parentData = parentRow.getData();
                this.context.store.calculateTotalWeekly(parentData as AssignmentDemand);
                parentRow.update(parentData);
            }
        });

        this.table = table;
        return this;
    }

    async applyTillEndLastAssignment() {
        if (this.changedAssignmentsAfter.length == 0 || !this.lastModifiedCell)
            return;

        let lastAssignment = this.changedAssignmentsAfter[this.changedAssignmentsAfter.length - 1];
        let [_, lastModifiedWeekNumber, lastModifiedWeekYear] = this.lastModifiedCell.getField().split("_").map(Number);
        let lastModifiedCellDate = this.getUTCWeekDay(lastModifiedWeekYear, lastModifiedWeekNumber, 1);
        let lastModifiedCellValue = this.lastModifiedCell.getValue();
        let endDateWeekNumber = getWeekNumber(lastAssignment.endDate || new Date());
        let endDateYear = getWeekYear(lastAssignment.endDate || new Date());
        let endDate = this.getUTCWeekDay(endDateYear, endDateWeekNumber, 5)

        Object.keys(lastAssignment.plannedWeeks).filter(key => key.startsWith("cw_")).forEach(key => {
            let [_, weekNumber, year] = key.split("_").map(Number);
            let currentDate = this.getUTCWeekDay(year, weekNumber, 5);

            if (lastModifiedCellDate <= currentDate && endDate >= currentDate) {
                lastAssignment.plannedWeeks[key] = lastModifiedCellValue;
            }
        });

        let currentRow = this.lastModifiedCell.getRow();
        let parentRow = currentRow.getTreeParent();
        if (parentRow) {
            let parentData = parentRow.getData();
            this.context.store.calculateTotalWeekly(parentData as AssignmentDemand);
            parentRow.update(parentData);
        }
        currentRow.update(lastAssignment);
    }

    async saveManuallyPlannedWeeks() {
        this.showLoading();
        const updatePromises: Promise<AssignmentDemand>[] = [];
        this.changedAssignmentsAfter.forEach(assignment => {
            updatePromises.push(this.context.store.updateAssignment(assignment));
        });
        await Promise.all(updatePromises);

        this.changedAssignmentsAfter.map(assignment => assignment.id).forEach(assignmentId => {
            fireBaseLog('assignment_table_manual_cell_change', { assignmentId: assignmentId });
        });


        this.changedAssignmentsBefore = [];
        this.changedAssignmentsAfter = [];
        this.updateSaveCancelButtons();

        this.hideLoading();
        showToast("Assignment suggested", "success");
    }

    cancelManuallyPlannedWeeks() {
        this.showLoading();
        const oldValuesMap = new Map(this.changedAssignmentsBefore.map(a => [a.id, a]));

        this.assignments.forEach(assignmentContainer => {
            assignmentContainer.assignments && assignmentContainer.assignments.forEach(assignment => {
                if (oldValuesMap.has(assignment.id)) {
                    Object.assign(assignment.plannedWeeks, oldValuesMap.get(assignment.id));
                }
            });
            this.context.store.calculateTotalWeekly(assignmentContainer);
        });

        this.table.destroy();
        this.addTable();

        this.changedAssignmentsBefore = [];
        this.changedAssignmentsAfter = [];
        this.updateSaveCancelButtons();

        this.hideLoading();
        showToast("Assignment suggest cancelled", "success");
    }

    isEditable(cell: CellComponent): boolean {
        let row = cell.getRow();
        let data = row.getData();
        return data.assignments ? false : true;
    }

    jumpToWeek() {
        this.selectCwCell(this.context.store.currentDate, "#ffd0a0");
        this.underlineCwCell(this.context.store.targetDate, "#FFA0A0");
    }

    underlineCwCell(date: Date, color: string) {
        const week = getWeekNumber(date);
        const cw = "cw_" + week + "_" + getWeekYear(date);
        const columns = this.table.getColumns();
        const columnExists = columns.some(column => column.getField() === `plannedWeeks.${cw}`);
        if (columnExists) {
            const column = this.table.getColumn(`plannedWeeks.${cw}`);
            column.getElement().style.borderBottom = '5px solid ' + color;
        }
    }

    selectCwCell(date: Date, color: string) {
        const week = getWeekNumber(date);
        const cw = "cw_" + week + "_" + getWeekYear(date);
        const columns = this.table.getColumns();
        const columnExists = columns.some(column => column.getField() === `plannedWeeks.${cw}`);
        if (columnExists) {
            const column = this.table.getColumn(`plannedWeeks.${cw}`);
            const element = column.getElement();
            element.style.backgroundColor = color;
        }
    }

    copyBtnFormatter(cell: CellComponent) {
        /** @type {PageContext} */
        const context = globalApp.app.context;

        var bvid = cell.getValue();
        let row = cell.getRow();
        let data = row.getData();

        var copyBtnHolder = document.createElement("div");
        copyBtnHolder.classList.add("text-truncate");

        if (context.user.hasEditPermission() && row.getData().assignments && row.getData().assignments.length > 0) {
            var copyBtn = document.createElement('button');
            copyBtn.type = "button";
            copyBtn.innerHTML = `<i class="bi bi-front"></i>`;
            copyBtn.style.lineHeight = "14px";
            copyBtn.style.padding = "4px";
            copyBtn.style.marginRight = "5px";
            copyBtn.classList.add("btn", "btn-sm", "btn-outline-secondary", "copy-button");

            const ctx = { ...context, projectAssignments: data };
            copyBtn.addEventListener("click", async (e) => {
                if (data.assignments.length == 0) return;
                $(".modal-dialog");
                await showModal("Demand copy", DemandCopyView, ctx);
            });

            copyBtnHolder.appendChild(copyBtn);
        }

        copyBtnHolder.appendChild(document.createTextNode(bvid));

        return copyBtnHolder;
    }

    buttonFormatter(cell: CellComponent) {
        /** @type {PageContext} */
        const context = globalApp.app.context;

        var bvid = cell.getValue();
        let row = cell.getRow();
        let data = row.getData();

        if (data.assignments) {
            if (bvid === '') bvid = "Resources (" + data.assignments.length + ")";

            //add buttons to cell (just the edit and delete buttons to start with)
            var buttonHolder = document.createElement("span");
            buttonHolder.appendChild(document.createTextNode(bvid + " "))

            //create edit button
            if (context.user.hasEditPermission()) {
                var editBt = document.createElement("button");
                editBt.type = "button";
                editBt.textContent = "+";
                editBt.style.lineHeight = "14px";
                editBt.classList.add("btn");
                editBt.classList.add("btn-sm");
                editBt.classList.add("btn-outline-secondary");

                editBt.addEventListener("click", async function (e) {
                    e.stopPropagation();
                    var ctx = Object.assign(context, { data: AssignmentDemand.createNew({ projectId: data.project.id }) });
                    await DemandDetailView.showAsModal(ctx);
                });

                buttonHolder.appendChild(editBt);
            }

            return buttonHolder;
        } else {
            if (!data.employeeId) return '<div class="name-cell-icon name-cell-icon--empty"></div>';

            const employee = (context.view as ProjectManagementView).allEmployeesMap.get(data.employeeId);
            const iconHtml = employee && employee.photo
                ? createEmployeePhotoElement(employee.photo, "name-cell-icon mx-2")
                : createNameIconElementWith2Initials(data.employeeId, "name-cell-icon mx-2 p-1", ".");

            const textHtml = employee ? `<span>${employee.employeeName}</span>` : `<span>Unknown Employee</span>`;

            const container = document.createElement("span");
            container.appendChild(convertHtmlToNode(iconHtml));
            container.appendChild(convertHtmlToNode(textHtml));
            container.addEventListener("click", (event) => {
                event.stopPropagation(); // Avoid row clicking
                if (employee) {
                    showResourceDetailModal(employee, context);
                }
            });

            return container;

        }
    }

    /**
     * Add calendar week columns to the table
     * @param {Array<object>} cwList array of calendar weeks e.g. {key:"cw_1_2021"}
     * @param {boolean} isEditable
     * @param {object} formatterParams
     * @returns {Tabulator.ColumnComponent[]} cwColumns
     */
    addCalendarWeekCols(cwList: CalendarWeek[], isEditable: boolean, formatterParams: { maxHours: number; employeesMap: Map<string, Employee>; }) {
        let cwColumns: any[] = [];
        let lastHoveredCell: { edit: () => void; } | undefined = undefined;
        cwList.forEach((cws) => {
            let yearCol = cwColumns.find((c) => c.title === cws.year);

            if (!yearCol) {
                yearCol = {
                    title: cws.year,
                    columns: [],
                    resizable: false
                };
                cwColumns.push(yearCol);
            }

            yearCol.columns.push({
                title: cws.cw,
                field: 'plannedWeeks.' + cws.key,
                width: 75,
                resizable: false,
                formatter: this.cwCellFormatter.bind(this),
                formatterParams: formatterParams,
                headerTooltip: "Calendar Week " + cws.cw + "/" + cws.year + ". " + cws.cw_start.toLocaleDateString() + " - " + cws.cw_end.toLocaleDateString(),
                editable: isEditable,
                cellMouseEnter: (e: MouseEvent, cell: CellComponent) => {
                    lastHoveredCell = cell;
                },
                cellMouseLeave: (e: MouseEvent, cell: CellComponent) => {
                    lastHoveredCell = undefined;
                },
                editor: (cell: CellComponent, onRendered: (arg0: () => void) => void, success: (arg0: number) => void, cancel: any) => {
                    let editor = document.createElement("input");
                    editor.style.width = "100%";
                    editor.setAttribute("type", "number");

                    let formattedValue = this.cwCellFormatter(cell);
              
                    let [week, year] = cell.getColumn().getField().split(".")[1].split("_").slice(1).map(Number);
                    let targetDate = this.getUTCWeekDay(year, week, 1);
                    let contractedHours = 40;

                    let rowData = cell.getRow().getData() as AssignmentDemand;
                    let employee = this.allEmployeesMap.get(rowData.employeeId);
                    
                    if (employee) {
                        employee.employmentPeriods.find((period) => {
                            let startDate = period.startDate ? new Date(period.startDate) : new Date(0);
                            let endDate = period.endDate ? new Date(period.endDate) : new Date(9999, 11, 31); 
                            if (startDate <= targetDate && endDate >= targetDate) {
                                contractedHours = period.contractedHours;
                                return true;
                            }
                            return false;
                        });
                    }

                    if (typeof formattedValue === 'string' && formattedValue.endsWith('%'))
                        formattedValue = formattedValue.slice(0, -1);

                    editor.value = formattedValue;

                    let reversedValue: number | null;
                    let successFunc = () => {
                        if (reversedValue)
                            return;

                        reversedValue = this.reverseCwCellFormatter(Number(editor.value), this.unit, contractedHours);
                        if (reversedValue < 0)
                            reversedValue = 0;
                        editor.value = reversedValue.toString();
                        success(reversedValue);
                        if (lastHoveredCell && lastHoveredCell != cell)
                            setTimeout(() => {
                                if (lastHoveredCell)
                                    return lastHoveredCell.edit();
                            }, 0);
                    }

                    editor.addEventListener("blur", successFunc);

                    onRendered(() => {
                        editor.focus();
                        editor.style.width = "100%";

                        // Add "%" symbol to the background of the input field
                        if (this.unit == "%") {
                            const getStringWidth = (string: string, element: HTMLElement) => {
                                let canvas = document.createElement('canvas');
                                let context = canvas.getContext('2d');
                                if (context) {
                                    context.font = getComputedStyle(element).font;
                                    return context.measureText(string).width + 5;
                                }
                                return 0;
                            }

                            editor.style.backgroundImage = "url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"20\" height=\"20\"><text x=\"0\" y=\"15\" fill=\"black\">%</text></svg>')";
                            editor.style.backgroundRepeat = "no-repeat";
                            editor.style.backgroundPosition = `${getStringWidth(formattedValue, editor)}px center`;

                            editor.addEventListener('input', function () {
                                this.style.backgroundPosition = `${getStringWidth(this.value, editor)}px center`;
                            });
                        }
                    });

                    return editor;
                },
                vertAlign: "middle",
                sorter: this.AbscenceOnTopSorter,
            });
        });
        return cwColumns;
    }

    AbscenceOnTopSorter = (a: string | null, b: string | null, aRow: RowComponent, bRow: RowComponent, _column: any, dir: string, _sorterParams: any) => {
        if (aRow.getData().role == AssignmentDemandRole.SuggestionsAssignmentRole || bRow.getData().role == AssignmentDemandRole.SuggestionsAssignmentRole)
            return (aRow.getData().role == AssignmentDemandRole.SuggestionsAssignmentRole ? 1 : -1) * (dir == "asc" ? 1 : -1);
        if (aRow.getData().role == AssignmentDemandRole.AbsenceAssigmentRole || bRow.getData().role == AssignmentDemandRole.AbsenceAssigmentRole)
            return (aRow.getData().role == AssignmentDemandRole.AbsenceAssigmentRole ? -1 : 1) * (dir == "asc" ? 1 : -1);
        if (a === null)
            a = '';
        if (b === null)
            b = '';
        return a.toString().localeCompare(b.toString());
    };

    /**
     * Create circles with color based on value and fist letters of names
     * @param {Tabulator.CellComponent} cell
     * @param {object} formatterParams 
     * @returns 
     */
    cwCellFormatter(cell: CellComponent) {
        var bvid = cell.getValue();
        let data = cell.getRow().getData();


        //color cells based on value
        if (bvid > 0)
            cell.getElement().style.backgroundColor = "#A6DF9A";
        else
            cell.getElement().style.backgroundColor = "";

        if (this.unit == "pd")
            return bvid ? bvid / 8 : 0;
        else if (this.unit == "%") {
            if (data.employeeId) {
                const employee = data.employee ?? (globalApp.context.view as ProjectManagementView).allEmployeesMap.get(data.employeeId);
                this.maxHours = employee?.weeklyHours ?? this.maxHours;
            }
            else if (data.assignments?.length > 0) {
                this.maxHours = 0;
                data.assignments.forEach((assignment: AssignmentDemand) => {
                    this.maxHours += ((globalApp.context.view as ProjectManagementView).allEmployeesMap.get(assignment.employeeId)?.weeklyHours ?? 40);
                });
                this.maxHours /= data.assignments.length;
            }
            return (bvid ? Math.round(bvid / this.maxHours * 100) : 0) + "%";
        }
        return bvid ? bvid : 0;
    }

    /**
     * @param {string | number} formattedValue
     * @param {string} unit
     * @param {number} maxHours
     * @returns {number}
     */
    reverseCwCellFormatter(formattedValue: number, unit: string, maxHours: number) {
        let bvid;

        if (unit == "pd") {
            bvid = formattedValue * 8;
        } else if (unit == "%") {
            bvid = formattedValue / 100 * maxHours;
        } else {
            bvid = formattedValue;
        }

        return Math.round(bvid);
    }



    applyFilteredView() {

        let filter = this.filteredViews[this.filteredView];
        this.table.setFilter((data: any) => !!filter(data));
        this.setInitialTableSort();
        this.resetTableScroll();
        this.context.params.filteredView = this.filteredView;
        Navigation.navigateHash(null, this.context.params, true);
    }

    async calculateRowsForEachFilter() {
        Object.keys(this.filteredViews).forEach((filterName) => {
            let filter = this.filteredViews[filterName as keyof typeof this.filteredViews];
            let rows = this.assignments.filter(filter);
            $(this).find(`.btn-filteredViews button[data-view='${filterName}']`)
                .text(`${filterName} (${rows.length})`);
        });
    }

    setInitialTableSort() {
        this.table.setSort([
            { column: "role", dir: "desc" },
        ]);
    }

    resetTableScroll() {
        if (this.table == undefined) return;

        const firstRow = this.table.getRowFromPosition(1);
        if (firstRow) firstRow.scrollTo();
    }


    /**
     * Custom filter function to determine if a row should be kept based on the header value.
     *
     * @param {string} headerValue - The value from the header to filter by.
     * @param {*} _rowValue - The value of the row (not used in this function).
     * @param {Object} rowData - The data of the row being evaluated.
     * @param {Object} filterParams - Additional parameters for the filter (not used in this function).
     * @returns {boolean} - Returns true if the row should be kept, false otherwise.
     */
    customEmployeeFilter(headerValue: string, _rowValue: RowComponent, rowData: any) {
        //keep item variable is used to keep the result of the loop in the main row
        var keepItem = false;
        var filterValue = headerValue.toLowerCase().replace(/\s/g, '');
        //child row
        if (!rowData.assignments) {
            if (rowData.employeeId === null)
                keepItem = false;
            else {
                var employee = rowData.employeeId.toLowerCase().replace(/\s/g, '');
                if (employee.length > 0 && employee.indexOf(filterValue) !== -1) {
                    keepItem = true;
                }
            }
            //main row
        } else {
            rowData.assignments.forEach((assignment: AssignmentDemand) => {
                if (assignment.employeeId === null) return;
                var employee = assignment.employeeId.toLowerCase().replace(/\s/g, '');
                if (employee.length > 0 && employee.indexOf(filterValue) !== -1) {
                    keepItem = true;
                }
            });
        }
        return keepItem;
    }

    customOriginatingProjectFilter(headerValue: string, _rowValue: RowComponent, rowData: any) {
        var keepItem = false;
        var filterValue = headerValue.toLowerCase().replace(/\s/g, '');
        if (!rowData.originatingProjectId)
            keepItem = false;
        else {

            if (rowData.assignments) {
                rowData.assignments.forEach((assignment: AssignmentDemand) => {
                    if (!assignment.originatingProjectId) return;
                    var originatingProject = assignment.originatingProjectId.toLowerCase().replace(/\s/g, '');
                    if (originatingProject.length > 0 && originatingProject.indexOf(filterValue) !== -1) {
                        keepItem = true;
                    }
                });
            }
            else {
                var originatingProject = rowData.originatingProjectId.toLowerCase().replace(/\s/g, '');
                if (originatingProject.length > 0 && originatingProject.indexOf(filterValue) !== -1) {
                    keepItem = true;
                }
            }
        }
        return keepItem;
    }

    subscribedAssignmentChangeHandler = async (assignment: AssignmentDemand, type: string) => {
        let rows = this.table.getRows();
        let row = rows.find(row => {
            let data = row.getData();
            return data.assignments.some((a: AssignmentDemand) => a.id === assignment.id);
        });
        if (!row) {
            row = rows.find(row => {
                let data = row.getData();
                return data.project.id === assignment.projectId;
            });
        }
        if (!row) return;

        if (type === "modified" || type === "added") {
            let data = row.getData();

            //changed assigned project
            if (assignment.projectId != data.project.id) {
                let newParentRow = rows.find(row => {
                    let data = row.getData();
                    return data.project.id === assignment.employeeId;
                });
                
                data.assignments = data.assignments.filter((a: AssignmentDemand) => a.id !== assignment.id);
                let calculatedAssignment = await this.context.store.calculateAssignmentWeeklyHours(assignment);

                if (newParentRow)
                {
                    let newParentData = newParentRow.getData() as AssignmentDemand;
                    if (newParentData.assignments)
                        newParentData.assignments.push(calculatedAssignment);
                    this.context.store.calculateTotalWeekly(newParentData);
                    newParentRow.update(newParentData);
                    newParentRow.reformat();
                }
            }
            else{
                let calculatedAssignment = await this.context.store.calculateAssignmentWeeklyHours(assignment);

                if (data.assignments.some((a: AssignmentDemand) => a.id === calculatedAssignment.id)) 
                    data.assignments = data.assignments.map((a: AssignmentDemand) => a.id === calculatedAssignment.id ? calculatedAssignment : a);
                else
                    data.assignments.push(calculatedAssignment);
    
                this.context.store.calculateTotalWeekly(data);
            }

            await row.update(data);
            console.log("assignment modified by other user");
        }
        else if (type === "removed") {
            let data = row.getData();
            data.assignments = data.assignments.filter((a: AssignmentDemand) => a.id !== assignment.id);
            this.context.store.calculateTotalWeekly(data);
            await row.update(data);
            console.log("assignment removed by other user");
        }
        row.reformat();
    }
}

